import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './ChatInput.module.css';

interface ChatInputProps {
    onSend: (content: string) => void;
    disabled: boolean;
}

const SendIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M8 12.6667V3.33334M8 3.33334L3.33333 8M8 3.33334L12.6667 8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const ChatInput: React.FC<ChatInputProps> = ({ onSend, disabled }) => {
    const [value, setValue] = useState('');
    const canSend = value.trim().length > 0 && !disabled;
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const autoResize = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, []);

    useEffect(() => {
        if (!disabled) textareaRef.current?.focus();
    }, [disabled]);

    useEffect(() => {
        autoResize();
    }, [value, autoResize]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSend) return;
        onSend(value);
        setValue('');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    return (
        <form className={styles.form} onSubmit={handleSubmit}>
            <textarea
                ref={textareaRef}
                className={styles.input}
                value={value}
                autoFocus
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about your parking lot..."
                disabled={disabled}
                rows={1}
            />
            <button
                type="submit"
                className={`${styles.sendButton} ${canSend ? styles.sendButtonActive : ''}`}
                disabled={!canSend}
            >
                <SendIcon />
            </button>
        </form>
    );
};

export default ChatInput;
